/**
 * This is the property of FRBNY and cannot be shared or disclosed without consent and strictly prohibited.
 * 
 * Stories: FR-TSM-003 (View Tour Calendar), TMEPSCVENT-11, TMEPSCVENT-26, TMEPSCVENT-27, TMEPSCVENT-28, TMEPSCVENT-29, TMEPSCVENT-31,
 *          TMEPSCVENT-57, TMEPSCVENT-58, TMEPSCVENT-59, TMEPSCVENT-3, TMEPSCVENT-61, TMEPSCVENT-62 (Reservation Management)
 * Generated by: AI-DLC Workflow
 * 
 * Vue Router configuration for TRS Public UI.
 * 
 * Security Features:
 * 1. Navigation guards enforce proper tour registration flow
 * 2. Users must go through: Privacy Agreement -> Calendar -> Registration
 * 3. Privacy Agreement is ALWAYS the landing page and entry point
 * 4. NO bookmarking allowed - all page reloads redirect to Privacy Agreement
 * 5. Direct URL access is blocked for all pages except Privacy Agreement
 * 6. Session state is cleared on every page reload
 */
import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';

const routes: RouteRecordRaw[] = [
    {
        path: '/',
        name: 'home',
        redirect: '/tours/privacy-agreement'
    },
    {
        path: '/tours',
        redirect: '/tours/privacy-agreement'
    },
    {
        path: '/tours/privacy-agreement',
        name: 'privacy-agreement',
        component: () => import('../views/PrivacyAgreementView.vue'),
                meta: {
            title: 'Request a Group Visit - FEDERAL RESERVE BANK OF NEW YORK',
            isLandingPage: true,
            requiresCalendarSelection: false
        }
    },
    {
        path: '/tours/calendar',
        name: 'tour-calendar',
        component: () => import('../views/TourCalendarView.vue'),
                meta: {
            title: 'Request a Group Visit - FEDERAL RESERVE BANK OF NEW YORK',
            requiresPrivacyAcceptance: true
        }
    },
    {
        path: '/tours/register',
        name: 'tour-registration',
        component: () => import('../views/TourRegistrationView.vue'),
                meta: {
            title: 'Request a Group Visit - FEDERAL RESERVE BANK OF NEW YORK',
            requiresPrivacyAcceptance: true,
            requiresCalendarDates: true
        }
    },
    {
        path: '/schools/search',
        name: 'school-search',
        component: () => import('../views/SchoolSearchView.vue'),
                meta: {
            title: 'Request a Group Visit - FEDERAL RESERVE BANK OF NEW YORK'
        }
    },
    {
        path: '/reservation-management',
        name: 'reservation-management',
        component: () => import('../views/ReservationManagementView.vue'),
                meta: {
            title: 'Request a Group Visit - FEDERAL RESERVE BANK OF NEW YORK'
        }
    },
    {
        path: '/alternatives/:token',
        name: 'alternative-response',
        component: () => import('../views/AlternativeResponseView.vue'),
                meta: {
            title: 'Alternative Dates - FEDERAL RESERVE BANK OF NEW YORK',
            isLandingPage: true // Exempt from privacy agreement (accessed via email)
        }
        },
        {
        path: '/penalty',
        name: 'penalty-page',
        component: () => import('../views/PenaltyPage.vue'),
        meta: {
            title: 'Request a Group Visit - FEDERAL RESERVE BANK OF NEW YORK',
            isLandingPage: true // Exempt from privacy agreement
        }
    },
    {
        path: '/expired-status',
        name: 'expired-status',
        component: () => import('../views/ExpiredStatusView.vue'),
        meta: {
            title: 'Reservation Expired - FEDERAL RESERVE BANK OF NEW YORK',
            isLandingPage: true // Exempt from privacy agreement (accessed via email)
        }
    },
    {
        path: '/tickets/generate',
        name: 'ticket-generation',
        component: () => import('../views/TicketGenerationView.vue'),
                meta: {
            title: 'Request a Group Visit - FEDERAL RESERVE BANK OF NEW YORK'
        }
    },
    {
        // Catch-all route - redirect any unknown paths to privacy agreement
        path: '/:pathMatch(.*)*',
        redirect: '/tours/privacy-agreement'
    }
];

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes
});

// Session flag to track if this is a fresh page load/reload
const SESSION_INITIALIZED_KEY = 'trsSessionInitialized';
const PRIVACY_ACCEPTED_KEY = 'tourPrivacyAccepted';
const NAVIGATION_ALLOWED_KEY = 'trsNavigationAllowed';

/**
 * Detect page reload/refresh
 * Returns true if this is a fresh page load (F5, bookmark, direct URL)
 */
function isPageReload(): boolean {
    const sessionInitialized = sessionStorage.getItem(SESSION_INITIALIZED_KEY);
    return sessionInitialized === null;
}

/**
 * Initialize session for tracking navigation state
 */
function initializeSession(): void {
    sessionStorage.setItem(SESSION_INITIALIZED_KEY, 'true');
    sessionStorage.setItem(NAVIGATION_ALLOWED_KEY, 'false');
}

/**
 * Check if user arrived from within the application flow
 */
function isNavigationAllowed(): boolean {
    return sessionStorage.getItem(NAVIGATION_ALLOWED_KEY) === 'true';
}

/**
 * Allow navigation within the application
 */
function allowNavigation(): void {
    sessionStorage.setItem(NAVIGATION_ALLOWED_KEY, 'true');
}

/**
 * Navigation guard to enforce proper tour registration flow
 * Prevents URL manipulation, bookmarking, and ensures privacy agreement is always the entry point
 *
 * Security Rules:
 * 1. All page reloads redirect to Privacy Agreement (anti-bookmark protection)
 * 2. Direct URL access only allowed to Privacy Agreement
 * 3. All other pages require proper navigation flow from Privacy Agreement
 * 4. Session state cleared on reload to prevent stale authentication
 */
router.beforeEach((to, from, next) => {
        // Set page title
    document.title = (to.meta.title as string) || 'Request a Group Visit - FEDERAL RESERVE BANK OF NEW YORK';

    // IMPORTANT: If navigating FROM privacy agreement, user has completed the first step
    // Allow navigation and ensure session is properly initialized
    if (from.name === 'privacy-agreement') {
        // User is navigating away from privacy agreement (normal flow)
        // Ensure session is initialized and navigation is allowed
        if (!sessionStorage.getItem(SESSION_INITIALIZED_KEY)) {
            initializeSession();
        }
        allowNavigation(); // Always allow navigation after leaving privacy page
        console.log('✅ Navigating from Privacy Agreement - session maintained');
    }

        // CRITICAL: Detect page reload/refresh (F5, bookmark, direct URL access)
    // If this is a page reload and NOT going to privacy agreement, redirect immediately
    // EXCEPTION: reservation-management route is allowed (used in emails)
    if (isPageReload() && from.name !== 'privacy-agreement') {
        console.log('Page reload detected - checking destination');

        // Initialize session tracking
        initializeSession();

                // Allow reservation-management, alternative-response, expired-status
        if (to.name === 'reservation-management' || to.name === 'alternative-response' || to.name === 'expired-status') {
            console.log('✅ Email link access allowed (reservation-management/alternative-response/expired-status)');
            allowNavigation();
            next();
            return;
        }

        // If trying to access any page other than privacy agreement on reload
        if (to.name !== 'privacy-agreement' && !to.meta.isLandingPage) {
            console.warn('⛔ BLOCKED: Page reload/bookmark detected. Redirecting to Privacy Agreement.');
            console.warn(`⛔ Attempted URL: ${to.fullPath}`);

            // Clear all session storage to prevent any stale state
            sessionStorage.clear();
            initializeSession();

            // Redirect to privacy agreement without preserving query params
            // This prevents bookmark manipulation
            next({
                name: 'privacy-agreement',
                replace: true
            });
            return;
        }

        // If going to privacy agreement on reload, allow it
        if (to.name === 'privacy-agreement') {
            console.log('✅ Page reload to Privacy Agreement - allowed');
            // Clear any stale session data
            sessionStorage.removeItem(PRIVACY_ACCEPTED_KEY);
            allowNavigation();
            next();
            return;
        }
    }

        // Check if this is initial app load (no 'from' route) and not going to privacy agreement
    // EXCEPTION: reservation-management route is allowed (used in emails)
    if (!from.name && to.name !== 'privacy-agreement' && !to.meta.isLandingPage) {
                // Allow reservation-management, alternative-response, expired-status
        if (to.name === 'reservation-management' || to.name === 'alternative-response' || to.name === 'expired-status') {
            console.log('✅ Email link direct access allowed (reservation-management/alternative-response/expired-status)');
            initializeSession();
            allowNavigation();
            next();
            return;
        }

        console.warn('⛔ BLOCKED: Direct URL access. Redirecting to Privacy Agreement.');
        console.warn(`⛔ Attempted URL: ${to.fullPath}`);

        // Clear session and redirect
        sessionStorage.clear();
        initializeSession();

        next({
            name: 'privacy-agreement',
            replace: true
        });
        return;
    }

        // If navigation is not allowed and not from privacy agreement, redirect to privacy agreement
    // Skip this check if coming from privacy agreement (already handled above)
    // EXCEPTION: reservation-management route is allowed (used in emails)
    if (!isNavigationAllowed() && to.name !== 'privacy-agreement' && from.name !== 'privacy-agreement') {
                // Allow reservation-management, alternative-response, expired-status
        if (to.name === 'reservation-management' || to.name === 'alternative-response' || to.name === 'expired-status') {
            console.log('✅ Email link navigation allowed (reservation-management/alternative-response/expired-status)');
            allowNavigation();
            next();
            return;
        }

        console.warn('⛔ BLOCKED: Invalid session state. Redirecting to Privacy Agreement.');
        sessionStorage.clear();
        initializeSession();
        next({
            name: 'privacy-agreement',
            replace: true
        });
        return;
    }

    // Check if route requires privacy acceptance (tour calendar and registration)
    if (to.meta.requiresPrivacyAcceptance) {
        const privacyAccepted = sessionStorage.getItem(PRIVACY_ACCEPTED_KEY);
        
        // If privacy not accepted, redirect to privacy agreement
        if (!privacyAccepted || privacyAccepted !== 'true') {
            console.warn('⛔ BLOCKED: Privacy agreement not accepted. Redirecting.');

            // Don't preserve query params to prevent bookmark manipulation
            next({
                name: 'privacy-agreement',
                replace: true
            });
            return;
        }
    }

    // Check if route requires calendar dates (tour registration)
    if (to.meta.requiresCalendarDates) {
        const dates = to.query.dates as string;
        
        // If no dates in URL, redirect to calendar
        if (!dates) {
            console.warn('⛔ BLOCKED: Missing required dates for registration.');
            next({ name: 'tour-calendar', replace: true });
            return;
        }
        
        // Validate dates format
        const dateArray = dates.split(',');
        if (dateArray.length === 0 || !dateArray.every(d => /^\d{4}-\d{2}-\d{2}$/.test(d))) {
            console.warn('⛔ BLOCKED: Invalid date format in URL.');
            next({ name: 'tour-calendar', replace: true });
            return;
        }
    }

    // Clear privacy acceptance flag when navigating away from tour flow
    // (except when navigating between tour flow pages)
    const tourFlowRoutes = ['privacy-agreement', 'tour-calendar', 'tour-registration'];
    if (from.name && tourFlowRoutes.includes(from.name as string) &&
        to.name && !tourFlowRoutes.includes(to.name as string)) {
        console.log('Leaving tour flow: Clearing privacy acceptance');
        sessionStorage.removeItem(PRIVACY_ACCEPTED_KEY);
    }

    next();
});

export default router;
